import React, { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Search, Copy, History, FileJson, LayoutGrid } from "lucide-react";
import api, { formatErr } from "../lib/api";

/* Wave W — PDO JSON dashboard (Process Design Object).
 *
 * Props: projectId
 * Mount: loads /pdo + /pdo/stats + /pdo/history. Auto-triggers /pdo/rebuild
 * once so existing projects see back-filled sections without manual action.
 */

const CELL = { padding: "6px 10px", border: "1px solid var(--pf-border)", fontSize: 12, verticalAlign: "top" };
const HDR = { ...CELL, background: "var(--pf-bg)", fontWeight: 600, color: "var(--pf-spark)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: "0.04em" };

const SECTION_TABS = [
  { key: "design_basis", label: "Design Basis" },
  { key: "simulation", label: "Simulation" },
  { key: "equipment", label: "Equipment" },
  { key: "lines", label: "Lines" },
  { key: "instruments", label: "Instruments" },
  { key: "control_loops", label: "Control Loops" },
  { key: "safety", label: "Safety" },
  { key: "piping", label: "Piping" },
  { key: "electrical", label: "Electrical" },
  { key: "civil", label: "Civil" },
  { key: "fire_protection", label: "Fire" },
  { key: "model_3d", label: "3D Model" },
  { key: "cost", label: "Cost" },
];

export default function PdoJsonTab({ projectId }) {
  const [pdo, setPdo] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [section, setSection] = useState("design_basis");
  const [showRaw, setShowRaw] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [err, setErr] = useState("");
  const [rebuilding, setRebuilding] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/projects/${projectId}/pdo`),
        api.get(`/projects/${projectId}/pdo/stats`),
      ]);
      setPdo(p.data);
      setStats(s.data);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  const loadHistory = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/pdo/history?limit=50`); setHistory(data.history); }
    catch (e) { /* silent */ }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try { await api.post(`/projects/${projectId}/pdo/rebuild`); await load(); await loadHistory(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setRebuilding(false); }
  };

  const runSearch = async () => {
    if (!query.trim()) { setSearchResults(null); return; }
    try { const { data } = await api.get(`/projects/${projectId}/pdo/search`, { params: { q: query } }); setSearchResults(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  // Initial load + one-shot auto-rebuild so existing projects see back-filled data.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
      // Auto-rebuild once — idempotent & inexpensive.
      try { await api.post(`/projects/${projectId}/pdo/rebuild`); await load(); } catch {}
      if (!cancelled) await loadHistory();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [projectId]);

  if (!pdo) {
    return <div data-testid="pdo-tab" style={{ padding: 20, color: "var(--pf-text-muted)" }}>{err || "Loading PDO…"}</div>;
  }

  const rawStr = JSON.stringify(pdo, null, 2);

  return (
    <div data-testid="pdo-tab" style={{ color: "var(--pf-text)" }}>
      {err && <div className="pf-sev pf-sev-error" style={{ padding: 8, margin: 8 }}>{err}</div>}

      {/* ─── Summary cards ─── */}
      {stats && (
        <div data-testid="pdo-summary-cards"
             style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, padding: 14 }}>
          <SummaryCard label="Design Basis"  value={`${stats.design_basis_pct}%`} testid="pdo-card-design-basis" />
          <SummaryCard label="Simulation"    value={stats.simulation_status.replace(/_/g, " ")} sub={`${stats.simulation_streams} streams`} testid="pdo-card-simulation" />
          <SummaryCard label="Equipment"     value={stats.equipment_count} sub="items" testid="pdo-card-equipment" />
          <SummaryCard label="Lines"         value={stats.lines_count} sub="items" testid="pdo-card-lines" />
          <SummaryCard label="Instruments"   value={stats.instruments_count} sub="items" testid="pdo-card-instruments" />
          <SummaryCard label="3D Model"      value={(stats.model_3d_status || "draft").replace(/_/g, " ")} testid="pdo-card-3d" />
        </div>
      )}

      {/* ─── Toolbar ─── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "0 14px 10px", borderBottom: "1px solid var(--pf-border)" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flex: 1,
                      background: "var(--pf-bg)", border: "1px solid var(--pf-border)",
                      borderRadius: 6, padding: "4px 10px" }}>
          <Search size={13} color="var(--pf-text-dim)" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  placeholder="Search PDO — e.g. V-101, LT-101, 6in-CR-001"
                  data-testid="pdo-search-input"
                  style={{ flex: 1, background: "transparent", border: "none", color: "var(--pf-text)", outline: "none", fontSize: 12 }} />
          {searchResults && <span style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>{searchResults.total} hits</span>}
          {query && (
            <button onClick={() => { setQuery(""); setSearchResults(null); }} className="btn btn-ghost" style={{ padding: 2, fontSize: 10 }}>✕</button>
          )}
        </div>
        <button onClick={rebuild} disabled={rebuilding} className="btn btn-ghost" data-testid="pdo-rebuild">
          <RefreshCw size={13} className={rebuilding ? "pf-spin" : ""} /> Rebuild
        </button>
        <button onClick={() => setShowHistory(!showHistory)} className="btn btn-ghost" data-testid="pdo-history-toggle">
          <History size={13} /> History ({history.length})
        </button>
        <button onClick={() => setShowRaw(!showRaw)} className="btn btn-ghost" data-testid="pdo-raw-toggle">
          {showRaw ? <LayoutGrid size={13} /> : <FileJson size={13} />} {showRaw ? "Visual" : "Raw JSON"}
        </button>
        <button onClick={() => {
          const blob = new Blob([rawStr], { type: "application/json" });
          const u = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = u; a.download = "PDO.json"; a.click();
          URL.revokeObjectURL(u);
        }} className="btn btn-ghost" data-testid="pdo-download"><Download size={13} /> Download</button>
        <button onClick={() => navigator.clipboard.writeText(rawStr)} className="btn btn-ghost" data-testid="pdo-copy">
          <Copy size={13} /> Copy
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start" }}>
        {/* ─── Main pane ─── */}
        <div style={{ flex: 1, overflow: "auto", maxHeight: "calc(100vh - 340px)" }}>
          {searchResults && searchResults.hits.length > 0 && (
            <SearchResults results={searchResults} />
          )}
          {!showRaw ? (
            <>
              <SectionTabs active={section} onChange={setSection} />
              <div style={{ padding: 14 }} data-testid={`pdo-section-pane-${section}`}>
                <SectionView section={section} pdo={pdo} />
              </div>
            </>
          ) : (
            <pre className="pf-code" data-testid="pdo-raw-json"
                  style={{ margin: 14, padding: 14, maxHeight: "calc(100vh - 380px)",
                                  overflow: "auto", fontSize: 11 }}>{rawStr}</pre>
          )}
        </div>

        {/* ─── History sidebar ─── */}
        {showHistory && (
          <div data-testid="pdo-history-pane"
               style={{ width: 360, borderLeft: "1px solid var(--pf-border)",
                        maxHeight: "calc(100vh - 300px)", overflow: "auto", padding: 12 }}>
            <div style={{ fontSize: 11, color: "var(--pf-text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Version history (last {history.length})
            </div>
            {history.length === 0 && <div style={{ fontSize: 12, color: "var(--pf-text-dim)" }}>No history yet.</div>}
            {history.map((h) => (
              <div key={h.id} data-testid={`pdo-history-${h.pdo_version}`}
                   style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--pf-border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--pf-spark)" }}>v{h.pdo_version}</span>
                  <span style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>{new Date(h.timestamp).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--pf-text)" }}>
                  <b style={{ color: "var(--pf-accent)" }}>{h.source_stage}</b> · {h.section}
                </div>
                <div style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>{h.message}</div>
                {h.diff && typeof h.diff === "object" && (
                  <div style={{ fontSize: 10, color: "var(--pf-text-dim)", marginTop: 2 }}>
                    {JSON.stringify(h.diff).substring(0, 100)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, testid }) {
  return (
    <div data-testid={testid}
         style={{ border: "1px solid var(--pf-border)", borderRadius: 6, padding: 10, background: "var(--pf-bg)" }}>
      <div style={{ fontSize: 10, color: "var(--pf-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: "var(--pf-spark)", marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>{sub}</div>}
    </div>
  );
}

function SectionTabs({ active, onChange }) {
  return (
    <div style={{ display: "flex", overflow: "auto", borderBottom: "1px solid var(--pf-border)", background: "var(--pf-bg)" }}>
      {SECTION_TABS.map((t) => (
        <button key={t.key} onClick={() => onChange(t.key)}
                data-testid={`pdo-tab-${t.key}`}
                style={{ background: "transparent", border: "none",
                              padding: "8px 14px", cursor: "pointer", fontSize: 12,
                              color: active === t.key ? "var(--pf-text)" : "var(--pf-text-muted)",
                              borderBottom: active === t.key ? "2px solid var(--pf-spark)" : "2px solid transparent",
                              whiteSpace: "nowrap" }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function SearchResults({ results }) {
  return (
    <div data-testid="pdo-search-results" style={{ padding: 14, background: "var(--pf-bg)", borderBottom: "1px solid var(--pf-border)" }}>
      <div style={{ fontSize: 11, color: "var(--pf-text-muted)", marginBottom: 6 }}>
        "{results.query}" found in {results.total} location(s):
      </div>
      <div style={{ maxHeight: 180, overflow: "auto" }}>
        {results.hits.map((h, i) => (
          <div key={i} style={{ padding: "3px 0", fontSize: 11 }}>
            <code style={{ color: "var(--pf-spark)", fontSize: 10 }}>{h.path}</code>
            <span style={{ marginLeft: 8, color: "var(--pf-text-muted)" }}>= {h.value.substring(0, 60)}{h.value.length > 60 ? "…" : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Per-section renderers ───────────────────────────────────────────────
function SectionView({ section, pdo }) {
  const data = pdo[section];
  if (section === "equipment") return <EquipmentTable items={data} />;
  if (section === "lines") return <LinesTable items={data} />;
  if (section === "instruments") return <InstrumentsTable items={data} />;
  if (section === "control_loops") return <ControlLoopsTable items={data} />;
  if (section === "simulation") return <SimulationView sim={data} />;
  return <DictView data={data} />;
}

function DictView({ data }) {
  if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
    return <div style={{ padding: 20, color: "var(--pf-text-muted)", fontSize: 12 }}>No data in this section yet. Run the corresponding stage to populate.</div>;
  }
  return (
    <pre style={{ background: "var(--pf-bg)", border: "1px solid var(--pf-border)",
                  borderRadius: 6, padding: 12, maxHeight: 500, overflow: "auto",
                  fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function TableView({ items, columns, emptyLabel, testid, rowKey }) {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    if (!filter) return items || [];
    const q = filter.toLowerCase();
    return (items || []).filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [items, filter]);
  if (!items || items.length === 0) {
    return <div style={{ padding: 20, color: "var(--pf-text-muted)", fontSize: 12 }}>{emptyLabel}</div>;
  }
  return (
    <div data-testid={testid}>
      <input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter rows…"
              className="pf-input" style={{ marginBottom: 8, fontSize: 11, width: 280 }} />
      <div style={{ fontSize: 11, color: "var(--pf-text-muted)", marginBottom: 6 }}>
        {filtered.length} of {items.length} rows
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>{columns.map((c) => <th key={c.key} style={HDR}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {filtered.map((r) => {
            const k = r[rowKey];
            return (
              <React.Fragment key={k}>
                <tr onClick={() => setExpanded(expanded === k ? null : k)}
                     data-testid={`${testid}-row-${k}`}
                     style={{ cursor: "pointer", background: expanded === k ? "var(--pf-surface-2)" : "transparent" }}>
                  {columns.map((c) => <td key={c.key} style={CELL}>{c.fmt ? c.fmt(r) : r[c.key] ?? "—"}</td>)}
                </tr>
                {expanded === k && (
                  <tr>
                    <td colSpan={columns.length} style={{ ...CELL, background: "var(--pf-bg)" }}>
                      <pre style={{ margin: 0, fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
                                           maxHeight: 300, overflow: "auto" }}>{JSON.stringify(r, null, 2)}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EquipmentTable({ items }) {
  return <TableView items={items} testid="pdo-equipment-table" rowKey="tag"
    emptyLabel="No equipment in PDO yet. Run B.2 PFD / B.3 P&ID to populate, or use I.1 TBE."
    columns={[
      { key: "tag", label: "Tag" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "service", label: "Service" },
      { key: "design_p", label: "Design P", fmt: (r) => `${(r.process_data || {}).design_pressure_barg ?? "—"} barg` },
      { key: "design_t", label: "Design T", fmt: (r) => `${(r.process_data || {}).design_temperature_c ?? "—"} °C` },
      { key: "material", label: "Material", fmt: (r) => (r.mechanical_data || {}).material_shell ?? "—" },
      { key: "pid", label: "P&ID", fmt: (r) => r.pid_sheet ?? "—" },
      { key: "status", label: "Status", fmt: (r) => {
          const s = r.status || {};
          if (s.po_placed) return "PO placed";
          if (s.requisition_issued) return "Req issued";
          if (s.mechanical_designed) return "Designed";
          if (s.process_defined) return "Process ready";
          return "—";
      } },
    ]} />;
}

function LinesTable({ items }) {
  return <TableView items={items} testid="pdo-lines-table" rowKey="line_number"
    emptyLabel="No lines in PDO yet. Run B.3 P&ID or D.1 Pipe Class to populate."
    columns={[
      { key: "line_number", label: "Line" },
      { key: "from_equipment", label: "From" },
      { key: "to_equipment", label: "To" },
      { key: "size_inch", label: "Size (in)" },
      { key: "pipe_class", label: "Class" },
      { key: "fluid", label: "Fluid" },
      { key: "design_pressure_barg", label: "DP (barg)" },
      { key: "design_temperature_c", label: "DT (°C)" },
      { key: "pid_sheet", label: "P&ID" },
    ]} />;
}

function InstrumentsTable({ items }) {
  return <TableView items={items} testid="pdo-instruments-table" rowKey="tag"
    emptyLabel="No instruments in PDO yet. Run B.3 P&ID or F.1 Instrument List to populate."
    columns={[
      { key: "tag", label: "Tag" },
      { key: "type", label: "Type" },
      { key: "measured_variable", label: "Variable" },
      { key: "equipment", label: "Equipment" },
      { key: "location", label: "Location" },
      { key: "loop_number", label: "Loop" },
      { key: "sil_level", label: "SIL" },
      { key: "pid_sheet", label: "P&ID" },
    ]} />;
}

function ControlLoopsTable({ items }) {
  return <TableView items={items} testid="pdo-control-loops-table" rowKey="loop_id"
    emptyLabel="No control loops in PDO yet. Run B.3 P&ID or F.3 Control & Logic."
    columns={[
      { key: "loop_id", label: "Loop" },
      { key: "type", label: "Type" },
      { key: "measured_variable", label: "Variable" },
      { key: "sensor_tag", label: "Sensor" },
      { key: "controller_tag", label: "Controller" },
      { key: "final_element_tag", label: "Final Element" },
      { key: "fail_position", label: "Fail Position" },
    ]} />;
}

function SimulationView({ sim }) {
  if (!sim) return <div style={{ padding: 20, color: "var(--pf-text-muted)", fontSize: 12 }}>Simulation not run yet.</div>;
  const streams = sim.streams || [];
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
        <SummaryCard label="Status" value={(sim.status || "not_run").replace(/_/g, " ")} testid="pdo-sim-status" />
        <SummaryCard label="Streams" value={streams.length} testid="pdo-sim-streams" />
        <SummaryCard label="Heating" value={`${(sim.utility_summary || {}).total_heating_kw || 0} kW`} testid="pdo-sim-heat" />
        <SummaryCard label="Cooling" value={`${(sim.utility_summary || {}).total_cooling_kw || 0} kW`} testid="pdo-sim-cool" />
      </div>
      <TableView items={streams} testid="pdo-streams-table" rowKey="id"
        emptyLabel="No streams — run B.1 Process Simulation."
        columns={[
          { key: "id", label: "Stream" },
          { key: "name", label: "Name" },
          { key: "from", label: "From" },
          { key: "to", label: "To" },
          { key: "temperature_c", label: "T (°C)" },
          { key: "pressure_barg", label: "P (barg)" },
          { key: "mass_flow_kgh", label: "Flow (kg/h)" },
          { key: "phase", label: "Phase" },
          { key: "density_kgm3", label: "Density" },
        ]} />
    </div>
  );
}
